/**
 * Minimal MP4 writer for one H.264 video track at a constant frame rate —
 * what the flythrough recorder (flythrough-video.ts) produces and nothing
 * more. Takes the encoder's chunks in AVCC form (VideoEncoder with
 * `avc: { format: 'avc' }`) plus its decoder description (the avcC record)
 * and lays them out as ftyp · mdat · moov, every sample in one chunk.
 *
 * Hand-written rather than a package: the layout for this one case is a
 * page of boxes, and the viewer bundle stays free of a muxer it would only
 * use on our own devices.
 */

export interface Mp4Colour {
    primaries: number;
    transfer: number;
    matrix: number;
    fullRange: boolean;
}

export interface Mp4Track {
    width: number;
    height: number;
    fps: number;
    /** avcC record, as VideoEncoder hands it in decoderConfig.description. */
    avcC: Uint8Array;
    colour: Mp4Colour | null;
    samples: Uint8Array[];
    /** 0-based indices of the key frames. */
    keyframes: number[];
}

// ISO/IEC 23091-2 code points for the colour spaces VideoEncoder reports.
const PRIMARIES: Record<string, number> = { bt709: 1, bt470bg: 5, smpte170m: 6, bt2020: 9, smpte432: 12 };
const TRANSFER: Record<string, number> = { bt709: 1, smpte170m: 6, linear: 8, 'iec61966-2-1': 13, pq: 16, hlg: 18 };
const MATRIX: Record<string, number> = { rgb: 0, bt709: 1, bt470bg: 5, smpte170m: 6, 'bt2020-ncl': 9 };

export function colourFrom(cs: VideoColorSpaceInit | undefined): Mp4Colour | null {
    if (!cs || !cs.primaries || !cs.transfer || !cs.matrix) return null;
    const p = PRIMARIES[cs.primaries];
    const t = TRANSFER[cs.transfer];
    const m = MATRIX[cs.matrix];
    if (p === undefined || t === undefined || m === undefined) return null;
    return { primaries: p, transfer: t, matrix: m, fullRange: !!cs.fullRange };
}

class Bytes {
    private parts: number[] = [];
    u8(v: number): this { this.parts.push(v & 0xff); return this; }
    u16(v: number): this { return this.u8(v >> 8).u8(v); }
    u32(v: number): this { return this.u8(v >>> 24).u8(v >>> 16).u8(v >>> 8).u8(v); }
    str(s: string): this { for (let i = 0; i < s.length; i++) this.u8(s.charCodeAt(i)); return this; }
    zeros(n: number): this { for (let i = 0; i < n; i++) this.u8(0); return this; }
    raw(b: Uint8Array): this { for (const v of b) this.u8(v); return this; }
    get length(): number { return this.parts.length; }
    done(): Uint8Array { return Uint8Array.from(this.parts); }
}

function box(type: string, ...body: Uint8Array[]): Uint8Array {
    const size = 8 + body.reduce((n, b) => n + b.length, 0);
    const out = new Uint8Array(size);
    new DataView(out.buffer).setUint32(0, size);
    for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    let o = 8;
    for (const b of body) { out.set(b, o); o += b.length; }
    return out;
}

function fullBox(type: string, version: number, flags: number, body: Bytes): Uint8Array {
    const head = new Bytes().u8(version).u8(flags >> 16).u8(flags >> 8).u8(flags).done();
    return box(type, head, body.done());
}

const MATRIX_UNITY = [0x10000, 0, 0, 0, 0x10000, 0, 0, 0, 0x40000000];

/** The finished file. Samples are referenced, not copied, until the Blob. */
export function writeMp4(t: Mp4Track): Blob {
    const n = t.samples.length;
    const timescale = t.fps * 100;
    const delta = 100;
    const mediaDuration = n * delta;
    const movieDuration = Math.round((n / t.fps) * 1000);

    const ftyp = box('ftyp', new Bytes().str('isom').u32(512).str('isom').str('iso2').str('avc1').str('mp41').done());
    const dataBytes = t.samples.reduce((s, b) => s + b.length, 0);
    const mdatHead = new Bytes().u32(8 + dataBytes).str('mdat').done();
    const dataOffset = ftyp.length + mdatHead.length;

    const mvhd = new Bytes().u32(0).u32(0).u32(1000).u32(movieDuration)
        .u32(0x00010000).u16(0x0100).zeros(10);
    MATRIX_UNITY.forEach(v => mvhd.u32(v));
    mvhd.zeros(24).u32(2);

    const tkhd = new Bytes().u32(0).u32(0).u32(1).u32(0).u32(movieDuration)
        .zeros(8).u16(0).u16(0).u16(0).u16(0);
    MATRIX_UNITY.forEach(v => tkhd.u32(v));
    tkhd.u32(t.width << 16).u32(t.height << 16);

    const mdhd = new Bytes().u32(0).u32(0).u32(timescale).u32(mediaDuration).u16(0x55c4).u16(0);
    const hdlr = new Bytes().u32(0).str('vide').zeros(12).str('VideoHandler').u8(0);
    const vmhd = new Bytes().u16(0).zeros(6);
    const dref = fullBox('dref', 0, 0, new Bytes().u32(1).raw(fullBox('url ', 0, 1, new Bytes())));

    const avc1Body = new Bytes().zeros(6).u16(1).u16(0).u16(0).zeros(12)
        .u16(t.width).u16(t.height).u32(0x00480000).u32(0x00480000).u32(0).u16(1)
        .zeros(32).u16(0x0018).u16(0xffff)
        .raw(box('avcC', t.avcC));
    if (t.colour) {
        avc1Body.raw(box('colr', new Bytes().str('nclx')
            .u16(t.colour.primaries).u16(t.colour.transfer).u16(t.colour.matrix)
            .u8(t.colour.fullRange ? 0x80 : 0).done()));
    }
    const stsd = fullBox('stsd', 0, 0, new Bytes().u32(1).raw(box('avc1', avc1Body.done())));
    const stts = fullBox('stts', 0, 0, new Bytes().u32(1).u32(n).u32(delta));
    const stssBody = new Bytes().u32(t.keyframes.length);
    t.keyframes.forEach(k => stssBody.u32(k + 1));
    const stss = fullBox('stss', 0, 0, stssBody);
    const stsc = fullBox('stsc', 0, 0, new Bytes().u32(1).u32(1).u32(n).u32(1));
    const stszBody = new Bytes().u32(0).u32(n);
    t.samples.forEach(s => stszBody.u32(s.length));
    const stsz = fullBox('stsz', 0, 0, stszBody);
    const stco = fullBox('stco', 0, 0, new Bytes().u32(1).u32(dataOffset));

    const moov = box('moov',
        fullBox('mvhd', 0, 0, mvhd),
        box('trak',
            fullBox('tkhd', 0, 3, tkhd),
            box('mdia',
                fullBox('mdhd', 0, 0, mdhd),
                fullBox('hdlr', 0, 0, hdlr),
                box('minf',
                    fullBox('vmhd', 0, 1, vmhd),
                    box('dinf', dref),
                    box('stbl', stsd, stts, stss, stsc, stsz, stco)))));

    return new Blob([ftyp, mdatHead, ...t.samples, moov] as BlobPart[], { type: 'video/mp4' });
}
